export type Point2 = [number, number];
export type Point3 = [number, number, number];

export type VisibleIJKValueMode = 'nearest' | 'trilinear';

export type FastObliqueOptions = {
  volumeId?: string;
  step?: number;
  valueMode?: VisibleIJKValueMode;
  dedupeIJK?: boolean;
  maxResults?: number;
};

export type VisibleIJKSamplingPayload = {
  viewportId?: string;
  dimensions: Point3;
  canvasSize: Point2;
  ijk00: Point3;
  dIJKdx: Point3;
  dIJKdy: Point3;
  minValue: number;
  maxValue: number;
  step?: number;
  valueMode?: VisibleIJKValueMode;
  dedupeIJK?: boolean;
  maxResults?: number;
};

type ScalarData =
  | Int8Array
  | Uint8Array
  | Uint8ClampedArray
  | Int16Array
  | Uint16Array
  | Int32Array
  | Uint32Array
  | Float32Array
  | Float64Array;

function sub3(a: Point3, b: Point3): Point3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function add3(a: Point3, b: Point3): Point3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function mul3(a: Point3, s: number): Point3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}

function roundIJK(p: Point3): Point3 {
  return [Math.round(p[0]), Math.round(p[1]), Math.round(p[2])];
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function toPoint3(values: ArrayLike<number>): Point3 {
  return [
    Math.trunc(Number(values[0])),
    Math.trunc(Number(values[1])),
    Math.trunc(Number(values[2])),
  ];
}

function toFloatPoint3(values: ArrayLike<number>): Point3 {
  return [Number(values[0]), Number(values[1]), Number(values[2])];
}

function getLinearIndex(
  i: number,
  j: number,
  k: number,
  dimI: number,
  dimJ: number,
): number {
  return i + j * dimI + k * dimI * dimJ;
}

function isInsideVolume(
  i: number,
  j: number,
  k: number,
  dimI: number,
  dimJ: number,
  dimK: number,
): boolean {
  return i >= 0 && i < dimI && j >= 0 && j < dimJ && k >= 0 && k < dimK;
}

export function createVisibleIJKSamplingPayload(
  viewport: any,
  minValue: number,
  maxValue: number,
  options: FastObliqueOptions = {},
): VisibleIJKSamplingPayload | null {
  const csImageData = options.volumeId
    ? viewport.getImageData(options.volumeId)
    : viewport.getImageData();
  const vtkImageData = csImageData?.imageData;
  const dimensions = csImageData?.dimensions ?? vtkImageData?.getDimensions?.();
  const worldToIndex = vtkImageData?.worldToIndex?.bind(vtkImageData);
  const canvas = viewport.getCanvas?.() ?? viewport.canvas;

  if (!dimensions || !worldToIndex || !canvas) {
    return null;
  }

  const width = Math.trunc(canvas.width || canvas.clientWidth || 0);
  const height = Math.trunc(canvas.height || canvas.clientHeight || 0);

  if (width <= 0 || height <= 0) {
    return null;
  }

  const world00 = viewport.canvasToWorld([0, 0]);
  const world10 = viewport.canvasToWorld([1, 0]);
  const world01 = viewport.canvasToWorld([0, 1]);

  const ijk00 = toFloatPoint3(worldToIndex(world00));
  const ijk10 = toFloatPoint3(worldToIndex(world10));
  const ijk01 = toFloatPoint3(worldToIndex(world01));

  return {
    dimensions: toPoint3(dimensions),
    canvasSize: [width, height],
    ijk00,
    dIJKdx: sub3(ijk10, ijk00),
    dIJKdy: sub3(ijk01, ijk00),
    minValue,
    maxValue,
    step: options.step,
    valueMode: options.valueMode,
    dedupeIJK: options.dedupeIJK,
    maxResults: options.maxResults,
  };
}

export function sampleVisibleLinearIndicesInRangeFastOblique(
  scalarData: ScalarData,
  payload: VisibleIJKSamplingPayload,
): Uint32Array {
  const [dimI, dimJ, dimK] = payload.dimensions;
  const [width, height] = payload.canvasSize;
  const frameSize = dimI * dimJ;
  const step = Math.max(1, Math.trunc(payload.step ?? 1));
  const valueMode = payload.valueMode ?? 'nearest';
  const dedupeIJK = payload.dedupeIJK ?? true;
  const maxResults = Number.isFinite(payload.maxResults)
    ? Math.max(0, Math.trunc(payload.maxResults ?? 0))
    : Number.POSITIVE_INFINITY;

  if (
    dimI <= 0 ||
    dimJ <= 0 ||
    dimK <= 0 ||
    width <= 0 ||
    height <= 0 ||
    !scalarData?.length ||
    maxResults <= 0
  ) {
    return new Uint32Array();
  }

  const getValueAtIJK = (
    i: number,
    j: number,
    k: number,
  ): number | undefined => {
    if (!isInsideVolume(i, j, k, dimI, dimJ, dimK)) {
      return undefined;
    }

    const index = getLinearIndex(i, j, k, dimI, dimJ);
    const value = scalarData[index];
    return Number.isFinite(value) ? value : undefined;
  };

  const sampleNearest = (
    continuousIJK: Point3,
  ): { linearIndex: number; value: number } | undefined => {
    const [i, j, k] = roundIJK(continuousIJK);
    const value = getValueAtIJK(i, j, k);

    if (value === undefined) {
      return undefined;
    }

    return {
      linearIndex: getLinearIndex(i, j, k, dimI, dimJ),
      value,
    };
  };

  const sampleTrilinear = (
    continuousIJK: Point3,
  ): { linearIndex: number; value: number } | undefined => {
    const [ci, cj, ck] = continuousIJK;

    if (
      ci < 0 ||
      ci > dimI - 1 ||
      cj < 0 ||
      cj > dimJ - 1 ||
      ck < 0 ||
      ck > dimK - 1
    ) {
      return undefined;
    }

    const i0 = Math.floor(ci);
    const j0 = Math.floor(cj);
    const k0 = Math.floor(ck);
    const i1 = Math.min(i0 + 1, dimI - 1);
    const j1 = Math.min(j0 + 1, dimJ - 1);
    const k1 = Math.min(k0 + 1, dimK - 1);
    const wi = ci - i0;
    const wj = cj - j0;
    const wk = ck - k0;

    const c000 = getValueAtIJK(i0, j0, k0);
    const c100 = getValueAtIJK(i1, j0, k0);
    const c010 = getValueAtIJK(i0, j1, k0);
    const c110 = getValueAtIJK(i1, j1, k0);
    const c001 = getValueAtIJK(i0, j0, k1);
    const c101 = getValueAtIJK(i1, j0, k1);
    const c011 = getValueAtIJK(i0, j1, k1);
    const c111 = getValueAtIJK(i1, j1, k1);

    if (
      c000 === undefined ||
      c100 === undefined ||
      c010 === undefined ||
      c110 === undefined ||
      c001 === undefined ||
      c101 === undefined ||
      c011 === undefined ||
      c111 === undefined
    ) {
      return undefined;
    }

    const c00 = lerp(c000, c100, wi);
    const c10 = lerp(c010, c110, wi);
    const c01 = lerp(c001, c101, wi);
    const c11 = lerp(c011, c111, wi);
    const c0 = lerp(c00, c10, wj);
    const c1 = lerp(c01, c11, wj);
    const value = lerp(c0, c1, wk);
    const [i, j, k] = roundIJK(continuousIJK);

    if (!isInsideVolume(i, j, k, dimI, dimJ, dimK)) {
      return undefined;
    }

    return {
      linearIndex: getLinearIndex(i, j, k, dimI, dimJ),
      value,
    };
  };

  const results: number[] = [];
  const seen = new Set<number>();
  const inRange = (value: number) =>
    value >= payload.minValue && value <= payload.maxValue;

  for (let y = 0; y < height; y += step) {
    const rowBase = add3(payload.ijk00, mul3(payload.dIJKdy, y + step * 0.5));

    for (let x = 0; x < width; x += step) {
      if (results.length >= maxResults) {
        return Uint32Array.from(results);
      }

      const continuousIJK = add3(rowBase, mul3(payload.dIJKdx, x + step * 0.5));
      const sampled =
        valueMode === 'trilinear'
          ? sampleTrilinear(continuousIJK)
          : sampleNearest(continuousIJK);

      if (!sampled || !inRange(sampled.value)) {
        continue;
      }

      if (dedupeIJK) {
        if (seen.has(sampled.linearIndex)) {
          continue;
        }

        seen.add(sampled.linearIndex);
      }

      results.push(sampled.linearIndex);
    }
  }

  return Uint32Array.from(results);
}

export function linearIndicesToIJKs(
  linearIndices: Uint32Array,
  dimensions: Point3,
): Point3[] {
  const [dimI, dimJ] = dimensions;
  const frameSize = dimI * dimJ;
  const points = new Array<Point3>(linearIndices.length);

  for (let index = 0; index < linearIndices.length; index += 1) {
    const linearIndex = linearIndices[index];
    const k = Math.floor(linearIndex / frameSize);
    const remainder = linearIndex - k * frameSize;
    const j = Math.floor(remainder / dimI);
    const i = remainder - j * dimI;
    points[index] = [i, j, k];
  }

  return points;
}

export function mergeLinearIndicesSorted(
  results: Uint32Array[],
): Uint32Array {
  const totalLength = results.reduce(
    (total, indices) => total + (indices?.length ?? 0),
    0,
  );

  if (totalLength <= 0) {
    return new Uint32Array();
  }

  const merged = new Uint32Array(totalLength);
  let offset = 0;

  results.forEach((indices) => {
    if (!indices?.length) return;
    merged.set(indices, offset);
    offset += indices.length;
  });

  merged.sort();

  let uniqueLength = 0;
  let previous = -1;

  for (let index = 0; index < merged.length; index += 1) {
    const value = merged[index];
    if (index > 0 && value === previous) {
      continue;
    }

    merged[uniqueLength] = value;
    uniqueLength += 1;
    previous = value;
  }

  return merged.slice(0, uniqueLength);
}

export function getVisibleIJKsInRangeFastOblique(
  viewport: any,
  voxelManager: any,
  minValue: number,
  maxValue: number,
  options: FastObliqueOptions = {},
): Point3[] {
  const payload = createVisibleIJKSamplingPayload(
    viewport,
    minValue,
    maxValue,
    options,
  );
  const scalarData = voxelManager?.getCompleteScalarDataArray?.() as
    | ScalarData
    | undefined;

  if (!payload || !scalarData) {
    return [];
  }

  return linearIndicesToIJKs(
    sampleVisibleLinearIndicesInRangeFastOblique(scalarData, payload),
    payload.dimensions,
  );
}
